import {defineSlotRecipe} from '@chakra-ui/react3'
import {
    CONTENT_MOTION,
    CONTENT_MOTION_ROOT,
    inColumn,
    LEVEL3_MOTION,
    LEVEL_HOLD,
    RESIZE_MOTION,
    RESOLVED_ITEM_INDEX
} from './flyout-stagger'

/**
 * Selector for the drilled-in state, published by the tree root as
 * `data-selection="auto" | "explicit"`.
 *
 * The two viewports share one markup tree: level 2 is always rendered for the
 * active entry, and only these rules decide whether it is on screen. `auto`
 * means nothing has been picked yet, so desktop falls back to the first entry
 * while mobile stays on the level-1 pane; `explicit` means the user chose, so
 * mobile swaps the panes. Nothing about the viewport reaches JavaScript.
 */
export const DRILLED_IN = '[data-selection="explicit"] &'

/**
 * The level-1 list's share of the desktop pane row: column 4.1 takes 15% of the
 * content container (STOK-17026, 13 Jul comment), which `panes` spans.
 *
 * Exported so a row laid out without the list reserves the same width; the
 * level-2 columns sit two boxes deeper and reconstruct it — see
 * `flyout-navigation-level2.recipe.js`.
 */
export const LEVEL1_COLUMN_FLEX = '0 0 15%'

/**
 * Selector for the level-3 state, published by the tree root as
 * `data-level3="true"`.
 *
 * Level 3 replaces every other pane on **both** viewports (STOK-17026 §3): the
 * level-1 list gives way alongside level 2, which is why the level-3 pane
 * carries a drill-out control where level 2's is mobile-only. As with
 * `DRILLED_IN`, the panes are all rendered and only these rules decide what is
 * on screen, so no breakpoint reaches JavaScript.
 */
export const DRILLED_IN_LEVEL3 = '[data-level3="true"] &'

/**
 * Mobile shows one pane at a time and slides between them: drilling in carries
 * the new pane in from the inline end while the old one leaves towards the
 * inline start, and drilling back out runs the same travel in reverse.
 *
 * The panes therefore move on `translate` rather
 * than being swapped with `display`. That is not a stylistic preference — a
 * `display: none` element cannot animate *or* be animated away, and toggling it
 * restarts any animation on the element from the top when it comes back — which
 * on that one property alone leaves the level-2 pane arriving blank for a beat
 * after every tap.
 *
 * Desktop keeps the `display` swap: it shows both panes side by side and has no
 * pane travel at all, so none of this applies there.
 */
const PANE_TRAVEL = {
    // `visibility` rides along so the pane waiting off-screen is out of the tab
    // order and the a11y tree. Being animatable, it holds the leaving pane
    // visible for the whole travel instead of cutting it at the first frame.
    transitionProperty: {base: 'translate, visibility', s: 'none'},
    transitionDuration: {base: 'slower'},
    transitionTimingFunction: {base: 'ease-out-strong'},
    _motionReduce: {
        transitionDuration: '0.01ms'
    }
}

/**
 * The travel plus the shared grid cell the panes stack in. The tool-nav row
 * takes `PANE_TRAVEL` alone: it travels with level 1 but has to stay the tree
 * root's last child (see `toolNavContainer`), so it never joins the cell.
 */
const PANE_SLIDE = {
    gridArea: {base: '1 / 1'},
    ...PANE_TRAVEL
}

/**
 * Every declaration below is stated at **both** breakpoints, and that is
 * load-bearing rather than defensive: a lone `base` value in Chakra applies from
 * `0px` up, so mobile's off-screen placement would otherwise reach desktop —
 * parking the level-2 pane at `visibility: hidden; translate: 100%` so the
 * megamenu shows level 1 alone, and hiding the level-1 list the moment a
 * category is picked. Desktop shows the panes side by side with no travel, so
 * it pins them present and swaps them by `display` alone.
 */

/** The pane on screen. */
const PANE_PRESENT = {
    translate: {base: 'none'},
    visibility: {base: 'visible'}
}

/**
 * Waiting off-screen at the inline end — where a pane comes *from* on the way
 * down, and returns to on the way back up.
 */
const PANE_AHEAD = {
    translate: {base: '100% 0', s: 'none'},
    visibility: {base: 'hidden', s: 'visible'},
    _rtl: {
        translate: {base: '-100% 0', s: 'none'}
    }
}

/**
 * Pushed off-screen at the inline start — where a pane goes when the next level
 * takes its place.
 */
const PANE_BEHIND = {
    translate: {base: '-100% 0', s: 'none'},
    visibility: {base: 'hidden', s: 'visible'},
    _rtl: {
        translate: {base: '100% 0', s: 'none'}
    }
}

/**
 * Whether a pane is in the mobile flow, stated as a `position` value.
 *
 * All three panes share grid cell `1 / 1` so they can slide past each other,
 * which means the cell is as tall as the *tallest* of them — including the two
 * nobody can see — which leaves the level-1 view with a band of empty space
 * between its list and its teasers, sized by the parked level-2 pane. Taking the
 * parked panes out of flow leaves the cell sized by the one on screen; each
 * slot states its own desktop value beside it, because only level 2 pins there.
 *
 * The matching `inset` is declared once per slot rather than here: level 2's
 * desktop `inset-block-start` is its sticky offset, and two declarations of the
 * same property in one recipe object would silently drop one.
 */
const PANE_PARKED = 'absolute'
const PANE_IN_FLOW = 'relative'

export const flyoutNavigationRecipe = defineSlotRecipe({
    slots: [
        'container',
        'panes',
        'level1Container',
        'itemsContainer',
        'item',
        'level2Container',
        'level3Container',
        'teasersContainer',
        'toolNavContainer'
    ],
    base: {
        container: {
            ...CONTENT_MOTION_ROOT,
            ...RESIZE_MOTION,
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-32',
            // The desktop seam between every flyout column, at both levels —
            // the design draws 40px at every one. Held here, above all three
            // column rows, because the level-2 row also adds it back in to
            // reconstruct the content container's width and the two readings
            // must agree. The rows keep their own mobile gaps, which stack
            // panes rather than separate columns and are unrelated to this.
            '--flyout-column-gap': '{spacing.spacing-40}',
            // The content's top inset, and the line the sticky columns pin to —
            // one definition so the two cannot drift. It sits here rather than
            // on the scrollport because `handleResize` fits the panel by
            // measuring this box, and an inset that varies by level has to be
            // inside that measurement or the panel steps instead of travelling.
            // STOK-17520 states the desktop figure only; mobile keeps its own.
            '--flyout-scroll-inset': {
                base: '{spacing.spacing-24}',
                s: '{spacing.spacing-40}'
            },
            // Held apart from the top because only the top varies by level, and
            // because the tool-nav row anchors to the foot: a row reading the
            // top would sit 20px low the moment level 3 tightened it.
            '--flyout-content-inset-end': {
                base: '{spacing.spacing-24}',
                s: '{spacing.spacing-40}'
            },
            paddingBlockStart: 'var(--flyout-scroll-inset)',
            paddingBlockEnd: 'var(--flyout-content-inset-end)',
            // Level 3 takes the whole panel and the design gives it a tighter
            // top. Scoped to the attribute the root already publishes, so the
            // columns' pins follow it down and still meet the content start.
            '&[data-level3]': {
                '--flyout-scroll-inset': {s: '{spacing.spacing-20}'}
            },
            // `body` is sized by its content, so its client height follows
            // this box — which `nav-resize` animates between two levels' heights
            // while the content inside keeps its natural size. Content below the
            // travelling edge still counts towards the scrollport's scrollable
            // region, so a growing level paints a scrollbar for the whole run:
            // STOK-17456 one box down, and level 3 is the tall one that shows it
            // (STOK-17551). The fades at either end sit 1rem low and carry the
            // same overrun, hence all three moving phases and not the resize
            // alone. `clip` rather than `hidden` keeps this box out of being a
            // scroll container, which would take the level-2 sticky pin off the
            // scrollport; the inline axis is left `visible`, the one pairing that
            // escapes the coercion `panes` documents. Desktop only — mobile pins
            // `body` to the available block size at both ends, so its height
            // never follows this box.
            '&[data-phase="exiting"], &[data-phase="resizing"], &[data-phase="entering"]': {
                overflowY: {s: 'clip'}
            },
            // anchor for the desktop tool-nav row, which leaves the flow — and
            // for the mobile row, which leaves it too while it travels
            position: 'relative'
        },
        panes: {
            // Mobile stacks every pane in one grid cell so they can slide past
            // each other; desktop sits them side by side in a row.
            display: {base: 'grid', s: 'flex'},
            gridTemplateColumns: {base: '100%'},
            flexDirection: {s: 'row'},
            gap: {s: 'var(--flyout-column-gap)'},
            alignItems: 'stretch',
            // The panes waiting off-screen would otherwise extend the scrollable
            // area on either axis. `clip` rather than `hidden` so this box does
            // not become a scroll container and strand the sticky columns inside
            // it, and because `clip` beside `visible` is the one mixed pairing
            // that escapes the coercion the flyout `body` documents — which is
            // what lets the two axes differ here.
            //
            // `PANE_PARKED` stops a parked pane *sizing* this box, but out of
            // flow is not out of the scrollable overflow region, and
            // `visibility: hidden` does not withhold it either. Only the block
            // axis shows it — the panes travel on the inline one, which the clip
            // above already covers. Parked at `inset-block-start: 0` a pane keeps
            // its full height, so a level-3 one retained after a drill-out left
            // the flyout scrollable past the level on screen, into a band holding
            // nothing (STOK-17509). Mobile alone parks a pane; desktop would only
            // lose the `1rem` block translate a level change fades through
            // (`LEVEL_MOTION`).
            overflowX: {base: 'clip'},
            overflowY: {base: 'clip', s: 'visible'},
            // Containing block for the parked panes below. Mobile only: desktop
            // parks nothing, and leaving it `static` there keeps the tool-nav
            // row's stacking-order reasoning (see `toolNavContainer`) untouched.
            position: {base: 'relative', s: 'static'}
        },
        // The level-1 pane on mobile: its category list and the teaser column
        // beneath it, as one box, because there they are one pane and a pane is
        // the unit that travels. Leaving the teaser column by `display` instead
        // puts it back at full strength on the first frame of a drill-out,
        // painting through the level-2 pane still sliding away, since `panes`
        // clips only on the inline axis (STOK-17364).
        //
        // Desktop has no travel and wants the list as its own flex item, so the
        // box is dissolved there and the layout is exactly the two slots below.
        level1Container: {
            display: {base: 'flex', s: 'contents'},
            flexDirection: 'column',
            // The seam between the list and the teaser column on mobile, the
            // same one `container` puts between its own rows.
            gap: 'spacing-32',
            minWidth: 0,
            ...PANE_SLIDE,
            ...PANE_PRESENT,
            // Stated at `base` alone, unlike the placements below: on desktop
            // this box generates none, so nothing here can leak there — bar
            // `visibility`, which inherits and which `PANE_BEHIND` restates.
            position: PANE_IN_FLOW,
            insetBlockStart: 'spacing-0',
            insetInline: 'spacing-0',
            // mobile drills in: level 1 slides out towards the inline start
            // while the next pane arrives, and leaves the flow so the panel is
            // sized by the level on screen. Both levels take it away
            // identically, so they share one rule.
            [`${DRILLED_IN}, ${DRILLED_IN_LEVEL3}`]: {
                ...PANE_BEHIND,
                position: PANE_PARKED
            }
        },
        itemsContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-12',
            listStyleType: 'none',
            margin: 0,
            padding: 0,
            flex: {s: LEVEL1_COLUMN_FLEX},
            // The tool-nav row overlaps the foot of the panes on desktop, and
            // per STOK-17026 §4.1 the level-1 list must not run behind it.
            // 60px covers the row (~30px) with room to spare, because the
            // button wraps to a second line on a long translated label.
            // TODO(STOK-17301): derive this from the row's measured height.
            // Same
            // reserve on the level-2 teaser column (§4.2.3), the other column
            // the row paints into.
            paddingBottom: {s: 'spacing-60'},
            // hide the container when no content is rendered — and with it the
            // desktop column, which is only this list
            _empty: {
                display: 'none'
            },
            // level 3 takes the whole panel, so level 1 goes on desktop too —
            // by `display` there, because desktop has no pane travel
            [DRILLED_IN_LEVEL3]: {
                display: {s: 'none'}
            }
        },
        item: {
            // The row animates, so it needs a box of its own. `NavigationHide`
            // merges onto it with `asChild` and leaves it `display: contents` by
            // default, which generates no box for the motion below to apply to.
            // Asked for as the property that recipe reads, not as `display`:
            // the merge puts the wrapper's declarations last, so a `display`
            // here would lose, while a property it reads resolves either way.
            '--navigation-hide-display': 'block',
            minWidth: 0,
            // Level 1 is the leftmost column, so it leads the cascade. Its rows
            // carry their own index inline; see `itemIndex` in the stagger module.
            ...RESOLVED_ITEM_INDEX,
            // `LEVEL3_MOTION` and not `LEVEL_MOTION`: this list stays on screen
            // through a category switch and only leaves for level 3.
            ...CONTENT_MOTION,
            ...LEVEL3_MOTION
        },
        // Placement box only — the pane's internal columns live in
        // `flyout/partials/flyout-navigation-level2.recipe.js`.
        level2Container: {
            // Present in the mobile layout at all times so it has somewhere to
            // travel from; `PANE_AHEAD` parks it off-screen until it is asked
            // for. Mobile's panel is viewport-height, so a pane waiting in the
            // same grid cell costs no layout.
            display: 'block',
            ...PANE_SLIDE,
            ...PANE_AHEAD,
            flex: {s: 1},
            // lets the pane's own columns size themselves instead of being
            // stretched by the flex row
            minWidth: 0,
            // The level-1 list is a sibling pane, not one of this pane's
            // columns, so the sticky columns inside cannot answer for it: they
            // travel within their own row and go with it once the taller
            // level-1 list scrolls the panel past its height (STOK-17341). The
            // whole pane pins instead, one level up, where `panes` — as tall as
            // the taller of the two — is the containing block that gives it the
            // level-1 list's own scroll to stay pinned through. When this pane
            // is the taller one there is no travel here and the pane scrolls as
            // a page, which is where its columns' own stickiness takes over
            // (STOK-17026 §4.2.1).
            position: {base: PANE_PARKED, s: 'sticky'},
            insetBlockStart: {base: 'spacing-0', s: 'var(--flyout-scroll-inset, 0px)'},
            insetInline: {base: 'spacing-0'},
            // A stretched flex item is as tall as the row, which would leave
            // nothing for the sticky above to travel.
            alignSelf: {s: 'flex-start'},
            // The pane the user asked for: it travels in from the inline end.
            [DRILLED_IN]: {
                display: 'block',
                ...PANE_PRESENT,
                position: {base: PANE_IN_FLOW, s: 'sticky'}
            },
            // Level 3 takes the whole panel, so this pane leaves the way level 1
            // did — towards the inline start on mobile, by `display` on desktop.
            [DRILLED_IN_LEVEL3]: {
                display: {s: 'none'},
                ...PANE_BEHIND,
                position: {base: PANE_PARKED, s: 'sticky'}
            }
        },
        // Placement box only — the pane's internal columns live in
        // `flyout/partials/flyout-navigation-level3.recipe.js`.
        level3Container: {
            // The placement box is always in the mobile layout even though its
            // contents are mounted on demand, which is what gives the pane
            // something to travel from. Desktop has no travel, so it keeps the
            // plain `display` swap.
            display: {base: 'block', s: 'none'},
            ...PANE_SLIDE,
            ...PANE_AHEAD,
            // No pane-level sticky here, unlike `level2Container`: level 3
            // takes the whole panel, so there is no sibling pane to scroll past
            // it and its own columns are the only thing that moves.
            position: {base: PANE_PARKED},
            insetBlockStart: {base: 'spacing-0'},
            insetInline: {base: 'spacing-0'},
            [DRILLED_IN_LEVEL3]: {
                display: 'block',
                ...PANE_PRESENT,
                position: {base: PANE_IN_FLOW},
                flex: {s: 1},
                minWidth: 0
            }
        },
        teasersContainer: {
            // Mobile-only, and the only column beside the level-1 list there, so
            // it animates as one unit rather than per teaser.
            ...inColumn(1),
            ...CONTENT_MOTION,
            // This column rides `level1Container` in and out, so nothing of its
            // own should play while it travels. See `LEVEL_HOLD`.
            ...LEVEL_HOLD,
            // display only on mobile
            display: {base: 'flex', s: 'none'},
            flexDirection: 'column',
            gap: 'spacing-12',
            // hide the container when no content is rendered
            _empty: {
                display: 'none'
            }
        },
        // Placement box only — the row's own layout lives in
        // `flyout/partials/flyout-tool-nav.recipe.js`.
        toolNavContainer: {
            // Last in the cascade on both viewports: the foot of the panel on
            // desktop, the last row in the flow on mobile. Column 3 is also
            // where it lands on desktop — the only part of the row painting
            // outside the level-1 reserve is the country selector, which sits
            // over the level-2 teaser column (STOK-17026 §5, and see
            // `teasers` in `flyout-navigation-level2.recipe.js`). The row
            // carries no `RESOLVED_ITEM_INDEX`, so this column term is its
            // whole delay.
            ...inColumn(3),
            ...CONTENT_MOTION,
            // Both apply, and in this order. Mobile slides the row out with the
            // level-1 pane, so `LEVEL_HOLD` stops anything of its own playing
            // on the way back; desktop keeps it through a category switch and
            // only drops it for level 3, which `LEVEL3_MOTION` then carries out
            // and back with the panes. The narrower scope wins where they meet.
            ...LEVEL_HOLD,
            ...LEVEL3_MOTION,
            // The row belongs to the level-1 view, so mobile carries it on the
            // panes' travel. It travels from here rather than from inside
            // `level1Container` because it has to stay the tree root's last
            // child, which the tab order and the stacking order below both read.
            ...PANE_TRAVEL,
            ...PANE_PRESENT,
            // `visibility` is deliberately left out of the transition, unlike
            // the panes: the parked row is pinned to `container`'s bottom edge,
            // which `RESIZE_MOTION` animates at the same moment, so a row held
            // visible across the travel drifts vertically as the panel resizes.
            // Cutting it at the first frame costs only the outbound leg — a
            // transition *to* `visible` applies immediately, so the return still
            // slides in from the first frame.
            transitionProperty: {base: 'translate', s: 'none'},
            // Restated because both spreads above declare it, and the later one
            // would otherwise replace it outright rather than merge.
            _motionReduce: {
                ...CONTENT_MOTION._motionReduce,
                ...PANE_TRAVEL._motionReduce
            },
            // Mobile keeps the row in the flow, last after the teasers. Desktop
            // pins it to the foot of the panel instead, overlapping the last
            // band of the panes — one row of markup serving both viewports.
            // `insetInline` is stated at both breakpoints because the parked
            // state below needs it on mobile too, where it costs nothing: on a
            // relatively positioned box it resolves to no shift.
            position: {base: PANE_IN_FLOW, s: 'absolute'},
            insetInline: 'spacing-0',
            // `bottom` resolves against the padding box, so a zero would land
            // the row below the panes it overlaps — and past the band
            // `itemsContainer` and the level-2 teaser column reserve for it.
            // Stated per viewport, unlike `insetInline`: on mobile the row is
            // `relative` while level 1 shows, where a non-zero value shifts it
            // rather than placing it. The parked states below restate it.
            bottom: {
                base: 'spacing-0',
                s: 'var(--flyout-content-inset-end, 0px)'
            },
            // The row spans the full width but only paints at its two ends, so
            // as a plain box it would swallow clicks on whatever navigation
            // content runs under its empty middle. Going transparent to the
            // pointer here lets those clicks through; the row's own recipe
            // switches its controls back on, so each control opts in by name
            // where it is defined.
            //
            // The row still has to sit *above* the panes to be clickable at
            // all — hit-testing takes the topmost box, and the level-1 column's
            // own reserve (see `itemsContainer`) covers exactly this band. It
            // wins on tree order alone: it and the panes are positioned boxes
            // with `z-index: auto` in the same stacking context (`container`
            // sets no z-index, so `position: relative` does not make it one),
            // and it is the last child. A `z-index` anywhere in the panes that
            // is not sealed inside a stacking context of its own would break
            // that. The level-2 and level-3 columns each seal their own, being
            // sticky, which is why `navigation-teaser`'s `zIndex: 1` stays put;
            // the level-1 list seals nothing, so a z-index there would escape.
            pointerEvents: {s: 'none'},
            // mobile drills in: the row belongs to the level-1 pane and travels
            // out of view alongside it, leaving the flow so the panel is sized
            // by the level on screen, while desktop still shows that pane
            [DRILLED_IN]: {
                ...PANE_BEHIND,
                position: {base: PANE_PARKED, s: 'absolute'},
                // absolute at both viewports here, so the inset applies at both
                bottom: 'var(--flyout-content-inset-end, 0px)'
            },
            // level 3 takes the whole panel on both viewports
            [DRILLED_IN_LEVEL3]: {
                display: {s: 'none'},
                ...PANE_BEHIND,
                position: {base: PANE_PARKED, s: 'absolute'},
                bottom: 'var(--flyout-content-inset-end, 0px)'
            }
        }
    }
})
